import { CouponVo, api } from "@/api";
import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from "@/util/safeLocalStorage";
import { makeAutoObservable } from "mobx";
import { VidMuseEvent } from "../common/events";
import { logger } from "../common/logger";

// 优惠券业务常量
export const RED_PACKET_COUPON_CODE = "SURPRISE_RED_PACKETS";
export const PROMO_DURATION_MS = 15 * 60 * 1000;
export const AUTO_SHOW_STAY_DURATION_MS = 30 * 1000;
const PROMO_START_TIME_KEY = "vidmuse_red_packet_start_time";
const LAST_SHOWN_DATE_KEY = "vidmuse_red_packet_last_shown_date";

/**
 * 首单优惠弹窗全局状态管理
 */
class FirstPurchasePromoStore {
  isOpen = false;
  couponData: CouponVo | null = null; // 活动优惠券数据

  // 从本地加载初始状态
  private _startTime: number | null = null;
  private _lastShownDate: string | null = null;

  // 跳转回调：点击 Claim 后要执行的操作
  private onClaimCallback: (() => void) | null = null;

  constructor() {
    const storedStartTime = safeLocalStorageGetItem(PROMO_START_TIME_KEY);
    this._startTime = storedStartTime ? parseInt(storedStartTime, 10) : null;
    this._lastShownDate = safeLocalStorageGetItem(LAST_SHOWN_DATE_KEY);
    makeAutoObservable(this);
  }

  get startTime() {
    return this._startTime;
  }

  get promoEndTime() {
    if (!this._startTime) return null;
    return this._startTime + PROMO_DURATION_MS;
  }

  /**
   * 是否应该展示促销 Banner 和应用折扣
   * 必须同时满足：1. 有优惠券资格；2. 倒计时未结束
   */
  get shouldShowPromo(): boolean {
    return !!this.couponData && this.isTimerActive;
  }

  /**
   * 倒计时是否处于激活状态
   */
  get isTimerActive(): boolean {
    if (!this._startTime) return false;
    const now = Date.now();
    return now - this._startTime < PROMO_DURATION_MS;
  }

  /**
   * 剩余时间（毫秒）
   */
  get remainingTime(): number {
    if (!this._startTime) return 0;
    const now = Date.now();
    return Math.max(0, this._startTime + PROMO_DURATION_MS - now);
  }

  /**
   * 当天是否已经弹出过
   */
  get wasShownToday(): boolean {
    const today = new Date().toDateString();
    return this._lastShownDate === today;
  }

  /**
   * 检查用户是否有红包优惠券
   */
  async checkEligibility() {
    try {
      const resp = await api.coupons.getEligibleCouponByCode({
        couponCode: RED_PACKET_COUPON_CODE,
      });
      this.couponData = resp.data || null;
    } catch (error) {
      console.error("Failed to check red packet eligibility:", error);
      this.couponData = null;
    }
  }

  /**
   * 尝试显示弹窗（包含规则校验）
   * @param onClaim 点击 Claim 按钮后的回调函数（通常是跳转 pricing）
   */
  tryShow(onClaim?: () => void) {
    if (!this.couponData) {
      onClaim?.();
      return;
    }

    if (this.wasShownToday) {
      // 当天已弹出过，直接执行回调（如跳转 pricing）
      onClaim?.();
    } else {
      // 未弹出过，显示弹窗并记录时间
      this.show(onClaim);
      this.recordPopupInteraction();
    }
  }

  /**
   * 记录弹窗交互（开始计时，记录当天已显示）
   */
  private recordPopupInteraction() {
    // 记录当天已显示
    const today = new Date().toDateString();
    this._lastShownDate = today;
    safeLocalStorageSetItem(LAST_SHOWN_DATE_KEY, today);

    // 开始倒计时（如果还没开始）
    if (!this._startTime || !this.isTimerActive) {
      const now = Date.now();
      this._startTime = now;
      safeLocalStorageSetItem(PROMO_START_TIME_KEY, now.toString());
    }
  }

  /**
   * 显示首单优惠弹窗
   * @param onClaim 点击 Claim 按钮后的回调函数
   */
  show(onClaim?: () => void) {
    logger.event(VidMuseEvent.FirstPurchasePromoDialogDisplayed);
    this.isOpen = true;
    this.onClaimCallback = onClaim || null;
  }

  /**
   * 关闭弹窗
   */
  close() {
    this.isOpen = false;
    this.onClaimCallback = null;
  }

  /**
   * 处理 Claim 按钮点击
   */
  clearPromoTimer() {
    this._startTime = null;
    safeLocalStorageRemoveItem(PROMO_START_TIME_KEY);
  }

  handleClaim() {
    this.isOpen = false;
    logger.event(VidMuseEvent.FirstPurchasePromoDialogClaimClick);

    // 执行回调
    if (this.onClaimCallback) {
      this.onClaimCallback();
      this.onClaimCallback = null;
    }
  }
}

export const firstPurchasePromoStore = new FirstPurchasePromoStore();
